import { ref } from 'vue'
import { signInAnonymously } from 'firebase/auth'
import {
  collection,
  doc,
  addDoc,
  getDoc,
  query,
  orderBy,
  onSnapshot,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore'
import { hasFirebase, getFirebase } from '@/firebase'
import { greeting, createDemoEngine } from '@/mock/engineFlow'
import { selectRoom, irAPagar, bookReservation } from '@/callables'
import { leerOrigen, sirveParaComisionar } from '@shared/origen'
import type { UiMessage } from '@/types'
import type { DatosViaje, CartPayload, CitaPayload, Origen } from '@shared/types'
import type { PaidPayload } from '@/payments'

/**
 * Motor del chat. Dos modos:
 *  - live: escribe/lee Firestore (la IA responde server-side por trigger).
 *  - mock: corre el flujo sobre el MOTOR REAL (reservationEngine/paymentEngine
 *    de @shared con clientes mock) — misma máquina de estados que producción.
 */
export function useChat() {
  const messages = ref<UiMessage[]>([])
  const isOpen = ref(false)
  const sending = ref(false)
  /** Nombre del viajero, si lo compartió (para etiquetar sus mensajes). */
  const visitorName = ref('Viajero')
  /** Lo que el asesor ya confirmó del viaje. Lo escribe el motor, no la UI. */
  const datosViaje = ref<DatosViaje | null>(null)
  /** El viaje que se está armando, espejado desde la reserva. */
  const carrito = ref<CartPayload | null>(null)
  /** Lo que el asesor ya recogió: nombre, teléfono, correo. */
  const contacto = ref<{ name?: string; phone?: string; email?: string } | null>(null)
  /**
   * Lo que el motor está haciendo ahora mismo ("Consultando precios…").
   *
   * Vacío cuando no hay nada corriendo. Lo escribe el servidor: la interfaz no
   * inventa la etiqueta, solo la muestra.
   */
  const trabajando = ref('')

  const demo = createDemoEngine()
  let convId: string | null = null
  let unsub: (() => void) | null = null
  let unsubConv: (() => void) | null = null
  // Estado de la venta en vivo (sobrevive a los snapshots de Firestore).
  let reservationId: string | null = null
  let selectedRoomRef: string | null = null

  /** Clave por sitio host para no cruzar conversaciones entre dominios. */
  const CONV_KEY = `tc_conv_id:${location.host}`

  /** Recupera la conversación previa (si existe y es del mismo visitante). */
  async function resumeConversation(fb: NonNullable<ReturnType<typeof getFirebase>>, uid: string): Promise<string | null> {
    let saved: string | null = null
    try {
      saved = localStorage.getItem(CONV_KEY)
    } catch {
      return null // navegador sin acceso a storage (modo privado estricto)
    }
    if (!saved) return null
    try {
      const snap = await getDoc(doc(fb.db, 'conversations', saved))
      if (snap.exists() && snap.data().visitorUid === uid && snap.data().status !== 'closed') {
        return saved
      }
    } catch {
      // si no se puede leer, empezamos una nueva
    }
    return null
  }

  async function init(): Promise<void> {
    if (!hasFirebase) {
      messages.value = [greeting()]
      return
    }
    const fb = getFirebase()!
    const cred = await signInAnonymously(fb.auth)

    // De dónde vino: es lo que después permite comisionar a la sucursal que lo
    // trajo. Ver `origen.ts`.
    const origen = leerOrigen(window.location.href, document.referrer)

    // Retomar la conversación previa del navegador, o crear una nueva.
    convId = await resumeConversation(fb, cred.user.uid)
    if (!convId) {
      const conv = await addDoc(collection(fb.db, 'conversations'), {
        visitorUid: cred.user.uid,
        status: 'open',
        channel: 'web',
        aiEnabled: true,
        ...(origen ? { origen: { ...origen, capturadoEn: new Date().toISOString() } } : {}),
        createdAt: serverTimestamp(),
      })
      convId = conv.id
      try {
        localStorage.setItem(CONV_KEY, convId)
      } catch { /* storage no disponible: seguimos sin persistir */ }
    } else if (origen) {
      /* Conversación que ya existía, y hoy llegó con un origen.
       *
       * Gana el PRIMERO que sirva para comisionar: quien trajo a la persona
       * hizo el trabajo, y si mañana vuelve por otra landing eso no le quita
       * el mérito a nadie. Pero una visita directa no tiene origen y por lo
       * tanto no bloquea: quien entró directo el lunes y volvió el martes por
       * la landing de Querétaro, cuenta para Querétaro. No había nada antes
       * que proteger.
       *
       * Se escribe sin `await` y sin romper si falla: la atribución no puede
       * retrasar ni tumbar la apertura del chat.
       */
      void (async () => {
        try {
          const ref = doc(fb.db, 'conversations', convId!)
          const snap = await getDoc(ref)
          const previo = snap.data()?.origen as Origen | undefined
          if (sirveParaComisionar(previo)) return
          await updateDoc(ref, {
            origen: { ...origen, capturadoEn: new Date().toISOString() },
          })
        } catch { /* la conversación importa más que saber de dónde vino */ }
      })()
    }

    const q = query(
      collection(fb.db, 'conversations', convId, 'messages'),
      orderBy('createdAt'),
    )
    unsub = onSnapshot(q, (snap) => {
      messages.value = snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as Omit<UiMessage, 'id'>),
      }))
    })
    // La conversación guarda el `reservationId` cuando la IA dispara la búsqueda.
    unsubConv = onSnapshot(doc(fb.db, 'conversations', convId), (d) => {
      const data = d.data()
      if (data?.reservationId) reservationId = data.reservationId as string
      const c = data?.contact as { name?: string; phone?: string; email?: string } | undefined
      if (c?.name) visitorName.value = c.name
      // Lo que el asesor ya recogió en la conversación. Sirve para no volver a
      // preguntar en el paso de pago lo que la persona ya escribió arriba.
      if (c) contacto.value = { name: c.name, phone: c.phone, email: c.email }
      const datos = data?.searchSlots as DatosViaje | undefined
      if (datos) datosViaje.value = datos
      /* Lo que el motor está haciendo AHORA MISMO, dicho por él.
       *
       * No es una etiqueta decorativa que gira sola: la escribe el servidor al
       * empezar cada herramienta y la borra al terminar. Si dice "Consultando
       * precios" es porque se están consultando precios. */
      trabajando.value = (data?.trabajando as string | undefined) ?? ''
      const items = data?.carrito as CartPayload['items'] | undefined
      if (items) {
        carrito.value = {
          items,
          total: (data?.carritoTotal as number | undefined) ?? 0,
          currency: (data?.carritoMoneda as string | undefined) ?? 'MXN',
        }
      }
    })
  }

  function pushUser(text: string, citaDe?: CitaPayload): void {
    messages.value.push({
      id: crypto.randomUUID(), kind: 'text', text, senderType: 'visitor',
      ...(citaDe ? { citaDe } : {}),
    })
  }

  /**
   * Mensaje escrito por el usuario.
   *
   * `citaDe` llega cuando deslizó una tarjeta para preguntar por ELLA. Viaja
   * guardado en el mensaje, no pegado al texto, por dos razones: el bot lo lee
   * como dato y no como una frase que tenga que interpretar, y la conversación
   * conserva a qué se refería cada pregunta cuando alguien la lea meses después.
   */
  async function sendMessage(text: string, citaDe?: CitaPayload): Promise<void> {
    const clean = text.trim()
    if (!clean || sending.value) return
    sending.value = true
    try {
      if (!hasFirebase) {
        pushUser(clean, citaDe)
        for (const reply of await demo.onText(clean)) messages.value.push(reply)
        return
      }
      const fb = getFirebase()!
      await addDoc(collection(fb.db, 'conversations', convId!, 'messages'), {
        kind: 'text',
        text: clean,
        senderType: 'visitor',
        // Se omite cuando no hay cita: Firestore rechaza `undefined`.
        ...(citaDe ? { citaDe } : {}),
        createdAt: serverTimestamp(),
      })
    } finally {
      sending.value = false
    }
  }

  /**
   * Acción de un botón de rich message (elegir hotel, pagar, …). `card` llega
   * cuando el pago se tokenizó en el navegador (PCI por delegación): en live
   * ese token viaja a `reservationBook`; el PAN nunca pasa por aquí.
   */
  async function sendAction(token: string, label: string, paid?: PaidPayload): Promise<void> {
    if (sending.value) return
    sending.value = true
    try {
      if (!hasFirebase) {
        pushUser(label)
        // El mock ejecuta el motor real (autorizar → reservar → capturar); el
        // token es simulado, así que no se usa aquí.
        for (const reply of await demo.onAction(token)) messages.value.push(reply)
        return
      }

      // "Me interesa este": se comporta como si el cliente lo escribiera, así
      // la IA lo toma en cuenta y sigue la conversación con naturalidad.
      if (token === 'like') {
        sending.value = false
        await sendMessage(label)
        return
      }

      // LIVE: las acciones invocan callables del núcleo (server-side). Las
      // respuestas (paso de pago, recibo) llegan al chat por realtime.
      // Agregar al viaje. Ya no es "elegir y pagar": el cliente arma su
      // paquete —hospedaje más los tours que quiera— y paga una vez al final.
      /* Elegir algo también CUENTA COMO HABLAR.
       *
       * Antes esto solo tocaba el carrito: la pizarra se actualizaba y el
       * asistente no se enteraba de nada. El cliente daba "Agregar al viaje",
       * veía su selección pintada, y en el chat seguían preguntándole cuál
       * quería. Elegir es la señal de compra más fuerte que hay y la estábamos
       * dejando pasar en silencio.
       *
       * Se escribe el mensaje DESPUÉS de que el carrito quedó, para que el
       * turno del asistente ya lea el viaje con la pieza dentro. Al revés
       * contestaría sobre el viaje anterior. */
      if (token.startsWith('select:')) {
        selectedRoomRef = token.slice('select:'.length)
        if (!reservationId) return
        await selectRoom(reservationId, selectedRoomRef, 'agregar')
        sending.value = false
        await sendMessage(label)
        return
      }

      if (token.startsWith('quitar:')) {
        const ref = token.slice('quitar:'.length)
        if (selectedRoomRef === ref) selectedRoomRef = null
        if (!reservationId) return
        await selectRoom(reservationId, ref, 'quitar')
        sending.value = false
        await sendMessage(label)
        return
      }

      if (token === 'checkout') {
        if (reservationId) await irAPagar(reservationId)
        return
      }

      if (token === 'pay' && paid && reservationId) {
        // El Brick no pide nombre del titular (MP lo valida con el banco):
        // si no viene, se usa el del visitante para el titular de la reserva.
        const parts = (paid.name || '').trim().split(/\s+/)
        const firstName = parts[0] ?? ''
        const lastName = parts.slice(1).join(' ') || firstName
        try {
          await bookReservation({
            reservationId,
            // El backend reserva el carrito completo. Esto solo sirve para las
            // cotizaciones abiertas antes de que el carrito existiera.
            ...(selectedRoomRef ? { roomRef: selectedRoomRef } : {}),
            holder: { firstName, lastName },
            occupants: [{ name: firstName, lastName, type: 'adult', age: 30 }],
            cardToken: paid.token,
            paymentMethodId: paid.paymentMethodId,
            issuerId: paid.issuerId,
            installments: paid.installments,
            identification: paid.identification,
            payerEmail: paid.email,
            payerPhone: paid.phone ?? '',
          })
          // El recibo (booking_summary) llega por realtime desde el backend.
        } catch {
          messages.value.push({
            id: crypto.randomUUID(), kind: 'text', senderType: 'bot',
            text: 'No se pudo completar la reserva; no se realizó ningún cargo. Un asesor te contactará.',
          })
        }
        return
      }
    } finally {
      sending.value = false
    }
  }

  function cleanup(): void {
    if (unsub) unsub()
    if (unsubConv) unsubConv()
  }

  return {
    messages, isOpen, sending, visitorName, datosViaje, carrito, contacto, trabajando,
    init, sendMessage, sendAction, cleanup,
  }
}
